/* Will it fit my car? (David's fit rules, 2026-09-30 and 2026-10-01). Runs every [data-pranos-fit] on the page.
   ONE rule set (rowsFor) for the home page, the order page and, later, Chatra.
   - Rear Side Door works on basically every car with a rear side window: lead with "fits your car".
   - Back Windshield: SUVs with a sunroof or glass roof (HoloGlass mounts on the roof glass) → "Also works".
     A car with no rear window at all (noBack in the list, such as the Polestar 4) → doesn't fit, whatever the roof.
   - Front Windshield: parked only; needs a compatible glass roof and cabin length.
   - Default to yes (David, 2026-10-09): when the answer depends on something we don't know yet (roof, cab, an
     unlisted car), the window shows "Our team confirms", which can be ordered, never a dead end. Only a known
     mismatch says "Doesn't fit". A car in the list can also name windows that fit outright (fits) or that our team
     confirms (team), such as the Cybertruck. Close misspellings ("cybertuck") still find the car.
   - Coupes, 2-door cars and two-seaters don't fit (David, 2026-10-12, via the App Development chat): a clear
     "Doesn't fit" on every window. A 2-door SUV (Wrangler, Bronco) has a back seat, so it keeps "Our team confirms"
     (David, 2026-10-13: "broncos work as long as they aren't the 2 seater ones"; HoloGlass needs enough distance and
     glass to mount on).
   - Pickups: Rear Side Door needs a back row with rear side windows. Crew cab fits; an extended cab with a back
     row usually fits (our team confirms); a regular cab doesn't. Back Windshield needs a crew cab + roof glass.
   Vehicle list: assets/pranos-fit-vehicles.js (window.PRANOS_FIT_DATA = { makes, models }, about 740 US models),
   loaded before this file. Without it, the small built-in list below still works. The built-in entries always count
   (food truck, extra spellings). A known make with an unknown model asks the body type instead of "we don't know".
   The fit is answered right here, live. window.PranosFit exposes the pure logic for tests.
   Each answer, pick and Order / Text us tap is also logged anonymously to the pranos-fit-log worker, only where
   Shopify's consent API allows analytics (see logFit).
   "Order" opens the kit with the window selected and the car filled in (with the body and cab the customer chose);
   on the kit page it does that in place. */
(() => {
  const PRODUCT = '/products/pranos-hologlass-kit';
  const SMS = 'sms:+14157611328';
  const VEHICLE_FIELD = 'properties[Vehicle year, make, and model]';
  const OPTION_VALUES = { rear: 'Rear Side Door Window', back: 'Back Windshield', front: 'Front Windshield' };

  const FIT = {
    win: {
      rear: { name: 'Rear Side Door', price: 289, slug: 'rear-side' },
      back: { name: 'Back Windshield', price: 429, slug: 'back-windshield' },
      front: { name: 'Front Windshield', price: 549, slug: 'front-windshield' },
    },
    bodies: [['sedan', 'Sedan'], ['suv', 'SUV / crossover'], ['hatchback', 'Hatchback'], ['minivan', 'Minivan'], ['pickup', 'Pickup'], ['coupe', 'Coupe / 2-door'], ['van', 'Van or food truck'], ['other', 'Something else']],
    status: {
      fits: { label: 'Fits your car', mark: 'yes', tone: 'good', ok: true, rank: 0 },
      also: { label: 'Also works', mark: 'yes', tone: 'good', ok: true, rank: 1 },
      team: { label: 'Our team confirms', mark: 'yes', tone: 'info', ok: true, rank: 2 },
      ask: { label: 'Depends on your roof', mark: 'maybe', tone: 'info', ok: false, rank: 3 },
      askcab: { label: 'Depends on your cab', mark: 'maybe', tone: 'info', ok: false, rank: 3 },
      photo: { label: 'Text us a photo first', mark: 'maybe', tone: 'warn', ok: false, rank: 4 },
      no: { label: 'Doesn’t fit', mark: 'no', tone: 'off', ok: false, rank: 5 },
    },
    // Built-in list: the fallback when the vehicle list didn't load. Specific models before their base model.
    cars: [
      { make: 'Honda', model: 'Civic Type R', alias: ['civic type r', 'type r', 'typer', 'type-r', 'ctr'], body: 'hatchback' },
      { make: 'Honda', model: 'Civic', alias: ['civic', 'civc', 'civik'], body: null, options: true, ask: 'The Civic has come as a sedan, a hatchback and a coupe. Which is yours?' },
      { make: 'Toyota', model: 'Camry', alias: ['camry', 'camery', 'camrey', 'camary'], body: 'sedan' },
      { make: 'Honda', model: 'Odyssey', alias: ['odyssey', 'odysey', 'oddysey', 'odessey', 'odyssy', 'odysse'], body: 'minivan' },
      { make: 'Tesla', model: 'Model Y', alias: ['model y', 'modely', 'model-y', 'tesla y'], body: 'suv', glassRoof: true, backAngle: 'steep', roofNote: 'The Model Y usually has a glass roof. On the Standard trim (late 2025 on) it’s covered, so pick No if you can’t see sky.' },
      { make: 'Ford', model: 'F-150', alias: ['f-150', 'f150', 'f 150'], body: 'pickup' },
      { make: 'Chevrolet', model: 'Tahoe', alias: ['tahoe', 'taho'], body: 'suv' },
      { make: 'Cadillac', model: 'Escalade', alias: ['escalade', 'escelade', 'escalate'], body: 'suv' },
      { make: 'Ford', model: 'Transit', alias: ['transit'], body: 'van' },
      { make: 'Mazda', model: 'Mazda3', short: 'Mazda3', alias: ['mazda3', 'mazda 3', 'mazda-3'], body: null, options: true, ask: 'The Mazda3 comes as a sedan and a hatchback. Which is yours?' },
      { make: 'BMW', model: '4 Series', alias: ['4 series', '4-series', '4series', '430i', '440i', 'm440i'], body: 'coupe' },
      { make: 'Ford', model: 'Mustang', alias: ['mustang'], body: 'coupe' },
      { make: '', model: 'food truck', alias: ['food truck', 'foodtruck', 'step van', 'stepvan', 'taco truck', 'box truck'], body: 'van', generic: true },
    ],
    // Makes for the built-in fallback only (the vehicle list brings its own, with misspellings).
    makes: [['Acura'], ['Audi'], ['BMW'], ['Buick'], ['Cadillac'], ['Chevrolet', 'chevy'], ['Chrysler'], ['Dodge'], ['Ford'], ['GMC'], ['Honda'], ['Hyundai'], ['Jeep'], ['Kia'], ['Lexus'], ['Lincoln'], ['Mazda'], ['Mercedes-Benz', 'mercedes', 'benz'], ['Nissan'], ['Ram'], ['Subaru'], ['Tesla'], ['Toyota'], ['Volkswagen', 'vw'], ['Volvo']],
    examples: ['2019 Camry', '2023 Model Y', '2022 F-150', '2021 Odyssey', 'Food truck'],
  };
  const NOUN = { sedan: 'sedans', suv: 'SUVs', hatchback: 'hatchbacks', minivan: 'minivans', pickup: 'pickups', coupe: 'coupes and 2-doors', van: 'vans' };
  // What goes on the order next to the car, so the team makes the screen for the right body and cab.
  const BODY_ORDER = { sedan: 'sedan', suv: 'SUV', hatchback: 'hatchback', minivan: 'minivan', pickup: 'pickup', coupe: 'coupe or 2-door', van: 'van' };
  const CAB_ORDER = { crew: 'crew cab', ext: 'extended cab', reg: 'regular cab' };
  const ORDER_MAX = 45;   // the order page's vehicle field takes 45 characters (templates/product.json max_length)
  const TRY_REAR = ' Try the Rear Side Door ✓.';
  const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

  // Lowercase, no accents or punctuation. canon() also splits letters from digits, so "CR-V" = "crv"... = "cr v", "F150" = "f 150".
  const plain = (s) => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/['’`]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();
  const split = (s) => s.replace(/([a-z])(\d)/g, '$1 $2').replace(/(\d)([a-z])/g, '$1 $2').replace(/\s+/g, ' ').trim();
  const canon = (s) => split(plain(s));
  const DRIVE = /^(4x4|4x2|4wd|2wd|awd|fwd|rwd|v6|v8|v10|v12)$/;   // never part of a model name; "4x4" would read as "x 4"

  // The lookup index, built on first use and rebuilt if the vehicle list arrives later.
  let index = null;
  let indexFrom;
  function getIndex() {
    const data = window.PRANOS_FIT_DATA;
    if (index && indexFrom === data) return index;
    indexFrom = data;
    const ok = !!(data && Array.isArray(data.models) && data.models.length && Array.isArray(data.makes));
    const cars = ok ? data.models.map((m) => ({
      make: m.make || '', model: m.model, alias: (m.aliases || []).slice(), body: m.body || null,
      options: m.options || null, ask: m.ask || '', glassRoof: !!m.glassRoof, roofNote: m.roofNote || '', cab: m.cab || null, cabWords: m.cabWords || null,
      noBack: !!m.noBack, backAngle: m.backAngle || null, fits: m.fits || null, team: m.team || null,
    })) : [];
    // Built-in entries: their spellings join the same model in the list; the ones the list lacks (food truck) are added.
    FIT.cars.forEach((c) => {
      const same = cars.find((d) => d.make.toLowerCase() === c.make.toLowerCase() && d.model.toLowerCase() === c.model.toLowerCase());
      if (!same) { cars.push(Object.assign({}, c, { alias: c.alias.slice() })); return; }
      c.alias.forEach((a) => { if (!same.alias.includes(a)) same.alias.push(a); });
      if (c.short && !same.short) same.short = c.short;
    });
    const makeList = ok ? data.makes.map((m) => [m.make].concat(m.aliases || [])) : FIT.makes;
    const makes = [];
    makeList.forEach((m) => m.forEach((a) => { const k = canon(a); if (k) makes.push({ k: ' ' + k + ' ', len: k.length, make: m[0] }); }));
    // An alias's length, for longest-match-first, leaves out a leading make ("toyota corolla" counts as "corolla",
    // so "Toyota Corolla Cross" still finds the Corolla Cross). raw breaks ties.
    const byMake = {};
    makes.forEach((m) => { (byMake[m.make] = byMake[m.make] || []).push(m); });
    const aliases = [];
    cars.forEach((c, i) => c.alias.forEach((a) => {
      const k = canon(a);
      if (!k) return;
      const pre = (byMake[c.make] || []).filter((m) => (k + ' ').indexOf(m.k.slice(1)) === 0 && k.length > m.len).reduce((n, m) => Math.max(n, m.len + 1), 0);
      aliases.push({ k: ' ' + k + ' ', len: k.length - pre, raw: k.length, car: c, i });
    }));
    index = { cars, aliases, makes, full: ok };
    return index;
  }
  // Letters apart (Levenshtein), stopping early once past max.
  function lettersApart(a, b, max) {
    if (Math.abs(a.length - b.length) > max) return max + 1;
    let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
    for (let i = 1; i <= a.length; i++) {
      const cur = [i];
      let low = i;
      for (let j = 1; j <= b.length; j++) {
        cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
        if (cur[j] < low) low = cur[j];
      }
      if (low > max) return max + 1;
      prev = cur;
    }
    return prev[b.length];
  }
  // A misspelled model ("cybertuck", "escalad"): the closest alias of 6+ letters, one letter off (two for 9+), or null.
  function nearHit(key, aliases) {
    const words = key.trim().split(' ');
    const tries = words.concat(words.slice(1).map((w, i) => words[i] + ' ' + w));
    let best = null;
    tries.forEach((t) => {
      if (t.replace(/ /g, '').length < 6 || /^\d+$/.test(t)) return;
      aliases.forEach((e) => {
        const k = e.k.trim();
        if (k.length < 6) return;
        const max = k.length >= 9 ? 2 : 1;
        const d = lettersApart(t, k, max);
        if (d > max || d === 0) return;
        if (!best || d < best.d || (d === best.d && k.length > best.e.k.length - 2)) best = { d, e, t };
      });
    });
    if (!best) return null;
    const at = key.indexOf(' ' + best.t + ' ');
    return { e: best.e, start: at + 1, end: at + best.t.length + 1 };
  }
  // Every entry whose words appear in key, with where they sit.
  const hits = (key, list) => {
    const out = [];
    list.forEach((e) => { const at = key.indexOf(e.k); if (at >= 0) out.push({ e, start: at + 1, end: at + e.k.length - 1 }); });
    return out;
  };
  const longest = (list) => list.reduce((a, h) => (!a || h.e.len > a.e.len
    || (h.e.len === a.e.len && ((h.e.raw || 0) > (a.e.raw || 0) || ((h.e.raw || 0) === (a.e.raw || 0) && h.e.i < a.e.i))) ? h : a), null);

  // A body word the user typed: [body, the word itself] ("passat wagon" = ['hatchback', 'wagon']), or null.
  const BODY_WORDS = [['hatchback', /\b(hatch|hatchback|wagon|sportwagen|sportwagon|avant)\b/], ['minivan', /\b(minivan)\b/], ['sedan', /\b(sedan)\b/],
    ['coupe', /\b(coupe|convertible|cabrio|cabriolet|roadster)\b/], ['suv', /\b(suv|crossover)\b/], ['pickup', /\b(pickup|truck)\b/], ['van', /\b(van)\b/]];
  function bodyIn(s, car) {
    for (const [body, re] of BODY_WORDS) {
      if (body === 'pickup' && car && car.generic) continue;
      const m = s.match(re);
      if (m) return [body, m[1]];
    }
    return null;
  }
  // Words that say the cab, doors or body, plus filler. What's left after the year, make and model is "more" (a trim,
  // or a word the read didn't use): the order then keeps the words the customer typed.
  const CAB_DOOR_WORDS = /(^|\s)(crew ?cab|crew ?max|super ?crew|super ?cab|extended ?cab|ext ?cab|access ?cab|king ?cab|club ?cab|double ?cab|quad ?cab|mega ?cab|regular ?cab|single ?cab|standard ?cab|([2-5]|two|three|four|five) ?(door|doors|dr))(?=\s|$)/g;
  const FILLER = new Set(['a', 'an', 'the', 'my', 'i', 'it', 'its', 'is', 'and', 'with', 'of', 'car', 'vehicle', 'hatch', 'hatchback', 'wagon', 'sportwagen', 'sportwagon', 'avant',
    'minivan', 'sedan', 'coupe', 'convertible', 'cabrio', 'cabriolet', 'roadster', 'suv', 'crossover', 'pickup', 'truck', 'van']);
  // A listed car's name without the year: "Volkswagen Jetta", and "Polestar 4" (not "Polestar Polestar 4").
  const carLabel = (car) => car.short || (car.make && car.model.toLowerCase().indexOf(car.make.toLowerCase()) === 0 ? car.model : [car.make, car.model].filter(Boolean).join(' '));

  function readCar(raw) {
    const idx = getIndex();
    const text = ' ' + plain(raw).replace(/\bmini van\b/g, 'minivan').replace(/\bpick up\b/g, 'pickup').replace(/\bcross over\b/g, 'crossover').replace(/\bhatch back\b/g, 'hatchback')
      .split(' ').filter((t) => !DRIVE.test(t)).join(' ') + ' ';
    const key = ' ' + split(text) + ' ';
    // Models: longest match first ("grand cherokee" beats "cherokee", "civic type r" beats "civic").
    // Makes: a make counts unless its word is only part of another make's model name ("genesis coupe" is a Hyundai).
    let models = hits(key, idx.aliases);
    if (!models.length) { const near = nearHit(key, idx.aliases); if (near) models = [near]; }
    const makeHits = hits(key, idx.makes).filter((h) => models.some((m) => m.e.car.make === h.e.make)
      || !models.some((m) => m.e.car.make && m.e.car.make !== h.e.make && m.start <= h.start && m.end >= h.end));
    const typedMakes = makeHits.map((h) => h.e.make);
    // A typed make wins: only its models count (plus generic entries such as the food truck).
    const best = longest(typedMakes.length ? models.filter((m) => !m.e.car.make || typedMakes.includes(m.e.car.make)) : models);
    const car = best ? best.e.car : null;
    const makeHit = longest(makeHits);
    const make = car && car.make ? car.make : (makeHit ? makeHit.e.make : null);
    // Year: never a number from the model's own name ("Toyota 86", "CX-30"). Two digits ("'19 Civic") only first or last.
    const own = best ? best.e.k.trim().split(' ') : [];
    let year = null;
    let yearTok = null;
    const y4 = key.trim().split(' ').find((t) => /^(19[5-9]\d|20[0-3]\d)$/.test(t) && !own.includes(t));
    if (y4) { year = +y4; yearTok = y4; }
    else if (car) {
      const t = text.trim().split(' ');
      const yy = [t[0], t[t.length - 1]].map((x) => (/^\d\d$/.test(x) && !own.includes(x) ? +x : null)).find((x) => x !== null && (x <= 30 || x >= 50));
      if (yy !== undefined) { year = yy <= 30 ? 2000 + yy : 1900 + yy; yearTok = String(yy).padStart(2, '0'); }
    }
    // Body words the user typed win, except words inside the model's own name ("GLC Coupe" is an SUV).
    const rest = best ? key.slice(0, best.start) + key.slice(best.end) : key;
    const bodyHit = bodyIn(rest, car) || (car && !car.body ? bodyIn(key, car) : null);
    let bodyWord = bodyHit ? bodyHit[0] : null;
    let bodyText = bodyHit ? bodyHit[1] : '';
    // The cab the user typed; else the list's own (trucks sold only as crew cabs, such as the Ridgeline).
    // A few makers use "Double Cab" or "Quad Cab" for 4 full doors (Toyota Tacoma, Dodge Dakota): the list says so (cabWords).
    const cw = (car && car.cabWords) || {};
    const cab = (cw.double && /(^|\s)double[ -]?cab(\s|$)/.test(text) ? cw.double
      : cw.quad && /(^|\s)quad[ -]?cab(\s|$)/.test(text) ? cw.quad
      : /(^|\s)(crew[ -]?cab|crew[ -]?max|super[ -]?crew|mega[ -]?cab|4[ -]?door|four[ -]door|4dr)(\s|$)/.test(text) ? 'crew'
      : /(^|\s)(extended[ -]cab|ext[ -]cab|super[ -]?cab|access[ -]cab|king[ -]cab|club[ -]cab|double[ -]?cab|quad[ -]?cab)(\s|$)/.test(text) ? 'ext'
      : /(^|\s)(regular[ -]cab|single[ -]cab|standard[ -]cab|2[ -]?door|two[ -]door|2dr)(\s|$)/.test(text) ? 'reg' : null) || (car && car.cab) || null;
    // Doors settle a model that came in two shapes: "Mini Cooper 2 door" is the coupe-like one.
    if (!bodyWord && car && !car.body && Array.isArray(car.options)) {
      const doors = /(^|\s)(2 ?door|two door|2 ?dr|3 ?door|three door)(\s|$)/.test(text) ? 2 : /(^|\s)([45] ?door|four door|five door|[45] ?dr)(\s|$)/.test(text) ? 4 : 0;
      const four = car.options.filter((o) => o !== 'coupe');
      if (doors === 2 && car.options.includes('coupe')) { bodyWord = 'coupe'; bodyText = '2-door'; }
      else if (doors === 4 && four.length === 1) { bodyWord = four[0]; bodyText = '4-door'; }
    }
    // Typed words the read didn't use ("xlt", "soft top", a make we don't list): then the order keeps the typed text.
    let left = key;
    [best].concat(makeHits).filter(Boolean).forEach((h) => { left = left.slice(0, h.start) + ' '.repeat(h.end - h.start) + left.slice(h.end); });
    const more = left.replace(CAB_DOOR_WORDS, ' ').trim().split(/\s+/).some((t) => t && t !== yearTok && !FILLER.has(t));
    const vintage = !!(year && year < 1990);
    const body = vintage ? 'other' : (bodyWord || (car && car.body) || null);
    const label = car && !car.generic ? carLabel(car) : null;
    const name = car ? (car.generic ? 'food truck' : [year, label].filter(Boolean).join(' ')) : (make ? [year, make].filter(Boolean).join(' ') : null);
    return { raw: String(raw || '').trim(), car, make, year, body, bodyWord, bodyText, cab, vintage, name, more, known: !!(car || bodyWord) };
  }

  // The car for the order and the text message: [for the order field (45 characters at most), in full].
  // The recognized name, or the typed text when it says more; plus the body and cab the customer chose.
  function vehicleFor(st) {
    const r = st.read;
    const base = r.car && !r.car.generic && !r.more ? r.name : r.raw;
    const parts = [];
    if (st.body && st.body !== 'other') {
      if (r.bodyText && st.body === r.bodyWord) parts.push(r.bodyText);
      else if (st.body !== (r.car ? r.car.body : null)) parts.push(BODY_ORDER[st.body]);
    }
    if (st.body === 'pickup' && CAB_ORDER[st.cab]) parts.push(CAB_ORDER[st.cab]);
    const have = ' ' + canon(base) + ' ';
    const extra = parts.filter((p) => have.indexOf(' ' + canon(p) + ' ') < 0).join(', ');
    const suffix = extra ? ' (' + extra + ')' : '';
    const full = base + suffix;
    if (full.length <= ORDER_MAX) return [full, full];
    // Too long for the field: keep the body and cab, shorten the typed text at a word break.
    const room = Math.max(0, ORDER_MAX - suffix.length);
    let cut = base.slice(0, room);
    if (base.charAt(room) && base.charAt(room) !== ' ' && cut.indexOf(' ') > 0) cut = cut.slice(0, cut.lastIndexOf(' '));
    return [(cut.trim() + suffix).slice(0, ORDER_MAX), full];
  }

  // What the checker starts from after a lookup (the chips change it).
  const stateFor = (r) => ({ read: r, body: r.body, roof: r.car && r.car.glassRoof ? 'yes' : 'unsure', slider: 'unsure', cab: r.cab || 'unsure', chosen: null });
  const whoFor = (r) => (r.car && !r.car.generic ? r.name : (r.car ? 'food truck' : (r.make ? r.name : 'car')));

  // [[window, status, reason], ...] in display order: the lead first, ✗ last.
  // Display order: the lead first, ✗ last (stable for equal ranks).
  const sortRows = (rows) => rows.map((r, i) => ({ r, i }))
    .sort((a, b) => (FIT.status[a.r[1]].rank - FIT.status[b.r[1]].rank) || (a.i - b.i))
    .map((x) => x.r);
  // A 2-door version of an SUV (Wrangler, Bronco): the list gives the model both shapes.
  const twoDoorSuv = (read) => !!(read && read.car && Array.isArray(read.car.options) && read.car.options.includes('suv'));
  const noCoupe = (body, read) => body === 'coupe' && !twoDoorSuv(read);

  function rowsFor(body, st, read) {
    const roof = st.roof;
    let rear, back, front;
    if (body === 'pickup' && st.cab === 'ext') return sortRows([
      ['rear', 'team', 'Trucks with a back row and rear side windows usually fit. Our team confirms yours before we make your screen.'],
      ['back', 'no', 'Needs a crew cab with 4 full doors, plus a sunroof or glass roof for the mount.' + TRY_REAR],
      ['front', 'team', 'Parked only. Our team confirms yours before we make your screen, and may ask for a photo of the windshield and roof.'],
    ]);
    if (body === 'pickup' && st.cab === 'reg') return sortRows([
      ['rear', 'no', 'A regular cab has no rear side window for the screen.'],
      ['back', 'no', 'Needs a crew cab with 4 full doors, plus a sunroof or glass roof for the mount.'],
      ['front', 'no', 'Parked only. Needs a crew cab with 4 full doors and a glass roof.'],
    ]);
    if (noCoupe(body, read)) return sortRows([
      ['rear', 'no', 'Made for cars with rear doors and a back row, not coupes, 2-doors or two-seaters.'],
      ['back', 'no', 'Made for 4-door SUVs and crew cabs, not coupes, 2-doors or two-seaters.'],
      ['front', 'no', 'Made for 4-door cars with a sunroof or glass roof, not coupes, 2-doors or two-seaters.'],
    ]);
    if (body === 'pickup' && st.cab !== 'crew') return sortRows([
      ['rear', 'team', 'Fits trucks with a back row and rear side windows, like crew and extended cabs. Our team confirms yours before we make your screen.'],
      ['back', 'team', 'Fits crew cabs with 4 full doors and a sunroof or glass roof. Our team confirms yours before we make your screen.'],
      ['front', 'team', 'Parked only. Fits crew cabs with 4 full doors and a glass roof. Our team confirms yours before we make your screen.'],
    ]);
    const rearOk = ['sedan', 'suv', 'pickup', 'hatchback'].includes(body);
    if (rearOk) rear = ['fits', 'Our most compatible window, and the one most customers choose.'];
    else if (body === 'minivan') rear = ['team', 'Sliding doors vary, so our team confirms yours before we make your screen.'];
    else if (body === 'coupe') rear = ['team', '2-door SUVs have smaller rear side glass and less room, so our team confirms yours before we make your screen.'];
    else if (body === 'van') rear = ['team', 'Side windows on vans and trucks vary, so our team confirms yours before we make your screen.'];
    else rear = ['team', read.vintage ? 'Older cars aren’t in our pattern library yet, so our team checks yours by hand and may ask for a photo or measurements.' : 'Fits most cars with rear side windows. Our team confirms yours before we make your screen.'];

    const car = read && read.car;
    if (car && car.noBack) back = ['no', 'The ' + esc(carLabel(car)) + ' has no rear window.' + TRY_REAR];
    // Back Windshield needs a fairly upright back window, like most SUVs; steeply sloped glass (Tesla Model Y, coupe-style
    // SUVs) doesn't work (David, launch day). backAngle comes from the vehicle data: steep = no, check = our team confirms.
    else if (car && car.backAngle === 'steep') back = ['no', 'The ' + esc(carLabel(car)) + '’s back window is too sloped. Back Windshield needs a fairly upright back window, like most SUVs.' + TRY_REAR];
    else if (body === 'sedan') back = ['no', 'Sedans have a sloped rear glass the screen can’t fit.' + TRY_REAR];
    else if (body === 'coupe') back = ['no', 'Made for 4-door SUVs and crew cabs, not coupes or 2-doors.' + TRY_REAR];
    else if (body === 'van' || body === 'other' || !body) back = ['team', 'Fits a fairly upright back window with a sunroof or glass roof for the mount. Our team confirms yours before we make your screen.'];
    else if (body === 'pickup' && st.slider === 'yes') back = ['no', 'A sliding back window needs another option. Text us a photo, and we’ll check for a film option.' + TRY_REAR];
    else if (roof === 'yes') {
      if (car && car.backAngle === 'check') back = ['team', 'Back Windshield needs a fairly upright back window, and yours slopes a little, so our team confirms the fit before we make your screen.'];
      else if (body === 'suv') back = ['also', 'A bigger display, for upright back glass like most SUVs. Your SUV has a glass roof, and HoloGlass mounts on it.'];
      else if (body === 'pickup') back = ['also', 'A bigger display. Your crew cab has a sunroof or glass roof, and HoloGlass mounts on it.' + (st.slider === 'no' ? '' : ' If your back window slides open, text us a photo first.')];
      else back = ['team', 'Uses your glass roof. ' + (body === 'hatchback' ? 'A rear wing or steep glass matters, so our' : 'Our') + ' team confirms the fit before we make your screen.'];
    } else if (roof === 'no') back = ['no', 'HoloGlass mounts on a glass roof for this window, and your ' + (body === 'suv' ? 'SUV' : body === 'pickup' ? 'truck' : 'car') + ' doesn’t have one.' + TRY_REAR];
    else back = ['team', 'Works on ' + (body === 'pickup' ? 'crew cabs' : (NOUN[body] || 'cars')) + ' with a sunroof or glass roof' + (body === 'suv' || body === 'hatchback' ? ' and a fairly upright back window' : '') + '. Our team confirms yours before we make your screen.'];

    if (body === 'van' || body === 'coupe' || body === 'other' || !body) front = ['team', 'Parked only. Our team confirms yours before we make your screen, and may ask for a photo of the windshield and roof.'];
    else if (roof === 'yes') front = ['team', 'Parked only. Uses your glass roof; our team confirms the cabin length before we make your screen.'];
    else if (roof === 'no') front = ['no', 'Parked only, and it mounts on a glass roof, which your car doesn’t have.' + (rearOk || body === 'minivan' ? TRY_REAR : '')];
    else front = ['team', 'Parked only. Works with a sunroof or glass roof and enough cabin length. Our team confirms yours before we make your screen.'];

    // Windows the vehicle list says fit this car outright (fits) or that our team confirms (team), unless an answer
    // above rules the window out (the roof or a sliding back window).
    const out = { rear, back, front };
    if (car) {
      (car.team || []).forEach((w) => { if (out[w] && out[w][0] !== 'no') out[w] = ['team', 'Our team confirms yours before we make your screen.']; });
      (car.fits || []).forEach((w) => {
        if (!out[w] || out[w][0] === 'no') return;
        out[w] = ['fits', w === 'front' ? 'Parked only. Fits the ' + esc(carLabel(car)) + ', and HoloGlass mounts on its glass roof.'
          : w === 'back' ? 'Fits the ' + esc(carLabel(car)) + '. A bigger display.' : rear[0] === 'fits' ? rear[1] : 'Fits the ' + esc(carLabel(car)) + '.'];
      });
    }
    return sortRows([['rear', ...out.rear], ['back', ...out.back], ['front', ...out.front]]);
  }

  // The headline over the rows: [mark, text, follow-up line or ''].
  function verdictFor(rows, st) {
    const lead = rows[0];
    const noun = st.body === 'pickup' && st.cab === 'ext' ? 'extended cabs' : st.body === 'coupe' ? '2-door SUVs' : (NOUN[st.body] || 'cars');
    const fitting = rows.filter((x) => x[1] === 'fits').map((x) => FIT.win[x[0]].name);
    if (fitting.length) return ['yes', fitting.join(' and ') + (fitting.length > 1 ? ' fit' : ' fits') + ' your ' + (st.body === 'pickup' ? 'truck' : 'car') + '.', ''];
    if (noCoupe(st.body, st.read)) return ['no', 'HoloGlass doesn’t fit coupes, 2-door cars or two-seaters.', 'Sorry about that. HoloGlass is made for cars with rear doors and a back row. Not a 2-door? Pick your body type above.'];
    if (st.body === 'pickup' && st.cab === 'reg') return ['no', 'A regular cab has no rear side window, so HoloGlass doesn’t fit it.', 'Not sure about your cab? Text us a photo, and we’ll check.'];
    if (st.body === 'pickup' && st.cab !== 'crew' && st.cab !== 'ext') return ['yes', 'Rear Side Door usually fits trucks with a back row.', 'Our team confirms yours before we make your screen. Picking your cab above gives a closer answer.'];
    if (FIT.status[lead[1]].ok) return ['yes', FIT.win[lead[0]].name + ' usually fits ' + noun + '.', 'Our team confirms yours before we make your screen.'];
    return ['maybe', 'Let’s check your car by hand.', ''];
  }

  // The line over the Order button: the fit is answered here, so it's about making the screen and shipping. HTML.
  const confirmLine = (status, who) => '<strong>We make your screen to match your ' + esc(who) + '’s window and ship it within 3 business days.</strong>' +
    (status === 'team' ? ' Our team checks yours before we make it, so we may ask you to text us a photo.' : '');

  const MARK = {
    yes: '<svg viewBox="0 0 14 14" aria-hidden="true"><path d="M3 7.4l2.6 2.6L11 4.4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    no: '<svg viewBox="0 0 14 14" aria-hidden="true"><path d="M4 4l6 6M10 4l-6 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
    maybe: '<svg viewBox="0 0 14 14" aria-hidden="true"><path d="M5.2 5.3a1.9 1.9 0 1 1 2.6 1.8c-.5.2-.8.6-.8 1.1v.5" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/><circle cx="7" cy="10.9" r=".95" fill="currentColor"/></svg>',
  };
  const MARK_WORD = { yes: 'Yes', no: 'No', maybe: 'Not yet known' };
  // Where the HoloGlass unit mounts, for the chosen window (shown under the rows).
  const MOUNT = {
    rear: 'The HoloGlass unit mounts inside with a suction cup on the opposite rear window, or on a sunroof or glass roof.',
    back: 'For the Back Windshield, the HoloGlass unit mounts inside on the roof glass, with the suction-cup mount.',
    front: 'For the Front Windshield, the HoloGlass unit mounts inside on the roof glass, with the suction-cup mount.',
  };
  const mark = (kind) => '<span class="pa-mark pa-mark--' + kind + '" role="img" aria-label="' + MARK_WORD[kind] + '">' + MARK[kind] + '</span>';
  const smsHref = (car) => SMS + '?&body=' + encodeURIComponent('Hi PRANOS, will HoloGlass fit my ' + (car || 'car') + '? I’ll send a photo of the window and the inside of the roof.');

  // On the kit page, choose the window and fill in the car without leaving the page.
  function selectOnThisPage(win, vehicle, scroll = true) {
    const value = OPTION_VALUES[win];
    const radio = [...document.querySelectorAll('variant-picker [data-option-name="Select Window"]')].find((c) => c.matches('input[type="radio"]') && c.value === value);
    const select = [...document.querySelectorAll('variant-picker select[name*="Select Window"]')][0];
    if (!radio && !select) return false;
    if (radio) { if (!radio.checked) radio.click(); }
    else { select.value = value; select.dispatchEvent(new Event('change', { bubbles: true })); }
    const fill = () => {
      const field = document.querySelector('[name="' + VEHICLE_FIELD + '"]');
      if (field && !field.value && vehicle) { field.value = vehicle; field.dispatchEvent(new Event('input', { bubbles: true })); }
    };
    fill();
    setTimeout(fill, 600);   // the variant change can re-render the form
    if (scroll) (radio ? radio.closest('fieldset') : select)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return true;
  }

  // Fit log (David, 2026-10-13: "are people using the fit tool... do we keep a record"): one short note per answer to
  // the pranos-fit-log worker (App Development; spec and privacy: pranos-support-agent-v2-fitlog\fitlog\DEPLOY.md).
  // What was typed, the car the tool read, its answer, the option picked, and Order / Text us taps. Under a random id
  // for this tab, kept in sessionStorage (not a cookie) and never put on the cart or the order: order matching stays
  // off until David decides. Sent only when Shopify's consent API allows analytics for this visitor (EEA, UK and
  // Switzerland: only after they agree in the cookie banner; privacy policy sections 7 and 13.4). Fire and forget:
  // the page never waits for it or shows anything. The example shown on load isn't logged.
  const LOG_URL = 'https://pranos-fit-log.jolly-wood-f820.workers.dev/fit-log';
  const LOG_PAGE = { index: 'home', product: 'product', page: 'compat' };
  const SID_KEY = 'pa-fit-sid';
  const SID_RE = /^[A-Za-z0-9_-]{8,40}$/;
  let sid = null;
  let consentWaiting = null;

  // cb(true) only when Shopify says so; anything missing or failing counts as no.
  function analyticsAllowed(cb) {
    const S = window.Shopify;
    const api = () => (S && S.customerPrivacy && typeof S.customerPrivacy.analyticsProcessingAllowed === 'function' ? S.customerPrivacy : null);
    const ask = (f) => { try { f(!!(api() && api().analyticsProcessingAllowed())); } catch { f(false); } };
    if (api()) return ask(cb);
    if (!S || typeof S.loadFeatures !== 'function') return cb(false);
    if (consentWaiting) return void consentWaiting.push(cb);
    consentWaiting = [cb];
    S.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => { const w = consentWaiting; consentWaiting = null; w.forEach(ask); });
  }

  // Made only once analytics are allowed, so nothing is stored in the browser before that.
  function tabId() {
    if (sid) return sid;
    try { sid = sessionStorage.getItem(SID_KEY); } catch { sid = null; }
    if (!sid || !SID_RE.test(sid)) {
      const b = crypto.getRandomValues(new Uint8Array(16));
      sid = btoa(String.fromCharCode(...b)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
      try { sessionStorage.setItem(SID_KEY, sid); } catch { /* private mode: this page only */ }
    }
    return sid;
  }

  function logFit(ev, st, chosen) {
    const page = LOG_PAGE[(document.body.className.match(/(?:^|\s)template-(\w+)/) || [])[1]];
    if (!page || !navigator.sendBeacon) return;
    const r = st.read || {};
    const rows = st.body ? rowsFor(st.body, st, r) : null;
    const entry = {
      v: 1, page, ev,
      q: r.raw ? r.raw.slice(0, 60) : null,
      read: { make: r.make || null, model: r.car && r.car.model ? r.car.model : null, year: r.year || null, body: st.body || null, cab: st.body === 'pickup' ? st.cab || null : null, known: !!r.known },
      rows: rows ? rows.map((x) => [FIT.win[x[0]].name, x[1]]) : null,
      verdict: rows ? verdictFor(rows, st)[0] : null,
      chosen: chosen || null,
    };
    analyticsAllowed((ok) => {
      if (!ok) return;
      try { navigator.sendBeacon(LOG_URL, JSON.stringify(Object.assign({ sid: tabId() }, entry))); } catch { /* never shown */ }
    });
  }

  let seq = 0;
  function FitChecker(root) {
    const id = 'pa-fit-' + (++seq);
    const example = root.getAttribute('data-example');
    let st = {};
    let lastRows = null, lastWho = '';
    root.innerHTML =
      '<form class="pa-fit-form" novalidate>' +
        '<label class="pa-fit-label" for="' + id + '-q">What do you drive?</label>' +
        '<div class="pa-fit-field"><input id="' + id + '-q" name="q" type="text" autocomplete="off" autocapitalize="words" spellcheck="false" maxlength="60" placeholder="Enter your car’s year, make and model"><button class="pranos-button" type="submit">Check my car</button></div>' +
        '<p class="pa-fit-examples"><span>Examples only—enter your own vehicle above:</span>' + FIT.examples.map((x) => '<button type="button" class="pa-chip" data-ex="' + esc(x) + '">' + esc(x) + '</button>').join('') + '</p>' +
        '<p class="pa-fit-notes">Minivan, van, food truck or classic? We check it by hand before we make your screen. Unusual vehicle? Text us a photo: <a href="sms:+14157611328">(415) 761-1328</a></p>' +
      '</form>' +
      '<div class="pa-fit-out" aria-live="polite"></div>';
    const form = root.querySelector('form');
    const input = root.querySelector('input');
    const out = root.querySelector('.pa-fit-out');
    form.addEventListener('submit', (e) => { e.preventDefault(); if (run(input.value)) fitLog('search'); reveal(); });
    root.querySelectorAll('[data-ex]').forEach((b) => b.addEventListener('click', () => { input.value = b.getAttribute('data-ex'); if (run(input.value)) fitLog('chip', 'example'); reveal(); }));
    // Fit log: searches and picks only once someone uses the tool (not the example shown on load); every Order and
    // Text us tap, which bubble up to here.
    const fitLog = (ev, chosen) => { if (ev === 'order_click' || ev === 'sms_click' || !st.example) logFit(ev, st, chosen); };
    root.addEventListener('click', (e) => {
      const a = e.target.closest && e.target.closest('a');
      if (!a) return;
      if (a.hasAttribute('data-order')) fitLog('order_click', 'window:' + a.getAttribute('data-order'));
      else if ((a.getAttribute('href') || '').startsWith('sms:')) fitLog('sms_click');
    });
    // Load Shopify's consent check on first use, so it's ready before an Order tap leaves the page.
    const warm = () => analyticsAllowed(() => {});
    root.addEventListener('pointerdown', warm, { once: true });
    root.addEventListener('focusin', warm, { once: true });

    // After a search, show that it worked (David, 2026-10-03: "I don't realize that it worked until I scroll down"):
    // the phone keyboard closes, the page glides to "We read that as ..." when it isn't in easy view, and that line
    // flashes once. Only on a search the visitor makes, never for the example shown on load.
    function reveal() {
      const first = out.firstElementChild;
      if (!first || first.classList.contains('pa-fit-hint')) return;
      input.blur();
      first.classList.remove('pa-fit-flash'); void first.offsetWidth; first.classList.add('pa-fit-flash');
      setTimeout(() => {
        const top = out.getBoundingClientRect().top;
        if (top < 80 || top > window.innerHeight * 0.45) {
          const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          out.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
        }
      }, 120);
    }

    function run(q, isExample) {
      q = String(q || '').trim();
      if (!q) { out.innerHTML = '<p class="pa-fit-hint">Type your car’s year, make and model, like “2019 Camry”.</p>'; input.focus(); return false; }
      st = Object.assign(stateFor(readCar(q)), { example: !!isExample });
      // Not in the list: show the general answer right away (default to yes); the body chips refine it.
      if (!st.read.car && !st.body) st.body = 'other';
      render();
      return true;
    }

    const chipGroup = (name, legend, options, value, hint) =>
      '<fieldset class="pa-fit-q"><legend>' + legend + '</legend><div class="pa-fit-chips" role="group">' +
      options.map((o) => '<button type="button" class="pa-chip" data-set="' + name + '" data-val="' + o[0] + '" aria-pressed="' + (value === o[0]) + '">' + esc(o[1]) + '</button>').join('') +
      '</div>' + (hint ? '<p class="pa-fit-hint">' + hint + '</p>' : '') + '</fieldset>';

    function render() {
      const r = st.read;
      let h = '';
      const older = r.vintage ? 'Older cars aren’t in our pattern library yet, so we check them by hand.' : '';
      if (!r.car && r.make) {
        // A make we know, a model we don't (yet): the body type gives the answer.
        h += '<p class="pa-fit-read">We know <strong>' + esc(r.make) + '</strong>, but not that model yet. <button type="button" data-act="edit">Change</button></p>';
        h += chipGroup('body', 'What body type is it?', FIT.bodies, st.body, older || (st.body ? '' : 'Pick one, and we’ll show which windows fit.'));
      } else if (!r.known) {
        h += '<div class="pa-fit-result"><h3>That exact model isn’t in our list yet, but most cars fit.</h3><p class="pa-fit-also">Our team confirms your car before we make your screen. Pick your body type below for a closer answer, or text us a photo.</p>' +
          '<div class="pa-fit-cta"><a class="pranos-button" href="' + smsHref(r.raw) + '">Text us a photo</a><a class="pranos-button pranos-button--secondary" href="mailto:support@pranos.ai?subject=' + encodeURIComponent('Will HoloGlass fit my car?') + '">Email us</a></div></div>';
        h += chipGroup('body', 'Body type', FIT.bodies, st.body);
      } else {
        const a = { sedan: 'a sedan', suv: 'an SUV', hatchback: 'a hatchback', minivan: 'a minivan', pickup: 'a pickup', coupe: 'a coupe', van: 'a van' };
        h += '<p class="pa-fit-read">We read that as <strong>' + esc(r.car && !r.car.generic ? r.name : (r.car ? 'a food truck or step van' : (a[r.bodyWord] || 'your car'))) + '</strong>. <button type="button" data-act="edit">Change</button></p>';
        if (r.car && r.car.options && !r.bodyWord && !st.body) h += '<p class="pa-fit-hint">' + esc(r.car.ask) + '</p>';
        h += chipGroup('body', 'Body type', FIT.bodies, st.body, older);
      }
      if (st.body) h += questions() + result();
      if (st.example) h = '<span class="pa-fit-example-label">Example result for a ' + esc(r.name || r.raw) + '. Type your car above to check yours.</span>' + h;
      out.innerHTML = h;
      wire();
      checkPicker();
    }

    function questions() {
      const r = st.read;
      const b = st.body;
      let h = '';
      if (b === 'pickup') h += chipGroup('cab', 'What kind of cab?', [['crew', 'Crew cab (4 full doors)'], ['ext', 'Extended cab (back row, small rear doors)'], ['reg', 'Regular cab (2 doors, no back row)'], ['unsure', 'Not sure']], st.cab, 'Rear Side Door needs a back row with rear side windows.');
      if (b === 'pickup' && st.cab !== 'crew') return h;
      if (b === 'pickup') h += chipGroup('slider', 'Does your back window slide open?', [['yes', 'Yes'], ['no', 'No'], ['unsure', 'Not sure']], st.slider);
      const roofMatters = ['sedan', 'suv', 'pickup', 'hatchback', 'minivan'].includes(b);
      if (roofMatters) {
        // Cars listed with a glass roof (Tesla) start on Yes, but the owner can still change it: not every trim has one open.
        const q = b === 'suv' ? 'Does your SUV have a sunroof or glass roof?' : b === 'pickup' ? 'Does your truck have a sunroof or glass roof?' : 'Does your car have a sunroof or glass roof?';
        const hint = r.car && r.car.glassRoof && r.car.roofNote ? esc(r.car.roofNote)
          : b === 'sedan' ? 'Only matters for the Front Windshield, which mounts on a glass roof.' : 'HoloGlass can mount on the roof glass. It decides the bigger windows.';
        h += chipGroup('roof', q, [['yes', 'Yes'], ['no', 'No'], ['unsure', 'Not sure']], st.roof, hint);
      } else if (r.car && r.car.roofNote) h += '<p class="pa-fit-hint">' + esc(r.car.roofNote) + '</p>';
      return h;
    }

    function result() {
      const r = st.read;
      const rows = rowsFor(st.body, st, r);
      lastRows = rows;
      const okRows = rows.filter((x) => FIT.status[x[1]].ok);
      const leadS = FIT.status[rows[0][1]];
      if (!st.chosen || !okRows.some((x) => x[0] === st.chosen)) st.chosen = okRows.length ? okRows[0][0] : null;
      const who = whoFor(r);
      lastWho = who;
      const v = verdictFor(rows, st);
      let h = '<div class="pa-fit-result"><h3>Your ' + esc(who) + '</h3>';
      h += '<p class="pa-fit-verdict">' + mark(v[0]) + '<span>' + v[1] + '</span></p>' + (v[2] ? '<p class="pa-fit-also">' + v[2] + '</p>' : '');
      const also = rows.filter((x) => x[1] === 'also');
      if (also.length) h += '<p class="pa-fit-also"><strong>Also works: ' + also.map((x) => FIT.win[x[0]].name).join(', ') + '</strong>, a bigger display (' + (st.body === 'suv' ? 'your SUV has a glass roof' : st.body === 'pickup' ? 'your crew cab has a sunroof or glass roof' : 'your car has a glass roof') + ').</p>';
      h += '<ul class="pa-fit-rows">';
      rows.forEach((x) => {
        const w = FIT.win[x[0]];
        const s = FIT.status[x[1]];
        const rid = id + '-w-' + x[0];
        h += '<li class="pa-fit-row' + (st.chosen === x[0] ? ' is-chosen' : '') + (s.ok ? '' : ' is-off') + '">' +
          '<input type="radio" name="' + id + '-win" id="' + rid + '" value="' + x[0] + '"' + (st.chosen === x[0] ? ' checked' : '') + (s.ok ? '' : ' disabled') + '>' +
          '<div class="pa-fit-row-main">' + mark(s.mark) + '<span class="pa-fit-win">' + w.name + '</span><span class="pa-fit-price">$' + w.price + '</span><span class="pa-status pa-status--' + s.tone + '">' + (x[1] === 'fits' && st.body === 'pickup' ? 'Fits your truck' : s.label) + '</span></div>' +
          '<p class="pa-fit-note">' + x[2] + '</p>' +
          (s.ok ? '<label for="' + rid + '"><span class="pa-sr">Choose ' + w.name + '</span></label>' : '') + '</li>';
      });
      h += '</ul>';
      // The back-seat rule and "Made for dusk to night" are said by the pages themselves (Home: the diagram and
      // What to know; the order and compatibility pages: their own copy), so the result doesn't repeat them
      // (claims audit item 18, 2026-10-01).
      // The mount line follows the chosen window (App Development chat review W1, 2026-10-02): the Back and Front
      // Windshield always mount on the roof glass.
      h += '<ul class="pa-fit-extra">';
      if (leadS.ok && MOUNT[st.chosen]) h += '<li>' + MOUNT[st.chosen] + '</li>';
      h += '<li>Light and moderate tint generally work; very dark tint reduces brightness.</li>';
      h += '<li>Display, window and advertising rules differ by state and city. <a class="pa-link" href="/pages/safety-and-compliance">How to check yours ›</a></li></ul>';
      const [vehicle, vehicleFull] = vehicleFor(st);
      if (st.chosen) {
        const w = FIT.win[st.chosen];
        h += '<p class="pa-fit-confirm">' + confirmLine(rows.find((x) => x[0] === st.chosen)[1], who) + '</p>' +
          '<div class="pa-fit-cta"><a class="pranos-button" href="' + PRODUCT + '?window=' + w.slug + '&vehicle=' + encodeURIComponent(vehicle) + '" data-order="' + st.chosen + '" data-vehicle="' + esc(vehicle) + '">Order ' + w.name + ' · $' + w.price + '</a>' +
          '<a class="pranos-button pranos-button--secondary" href="' + smsHref(vehicleFull) + '">Text us a photo</a></div>';
        // Every other window that fits gets its own order link (launch-day testers wanted the bigger window and only
        // saw the Rear Side Door button).
        const others = okRows.filter((x) => x[0] !== st.chosen);
        if (others.length) h += '<p class="pa-fit-more">Or order: ' + others.map((x) => { const o = FIT.win[x[0]]; return '<a class="pa-link" href="' + PRODUCT + '?window=' + o.slug + '&vehicle=' + encodeURIComponent(vehicle) + '" data-order="' + x[0] + '" data-vehicle="' + esc(vehicle) + '">' + o.name + ' · $' + o.price + ' ›</a>'; }).join('') + '</p>';
      } else if (noCoupe(st.body, r)) {
        h += '<p class="pa-fit-confirm"><strong>Have another car?</strong> Type it above to check it. Questions? Text us at <a class="pa-link" href="' + SMS + '">(415) 761-1328</a>.</p>';
      } else {
        h += '<p class="pa-fit-confirm"><strong>Send us a photo of the window and the inside of the roof.</strong> We’ll tell you what fits before you order.</p>' +
          '<div class="pa-fit-cta"><a class="pranos-button" href="' + smsHref(vehicleFull) + '">Text us a photo</a><a class="pranos-button pranos-button--secondary" href="tel:+14157611328">Call (415) 761-1328</a></div>';
      }
      return h + '</div>';
    }

    function wire() {
      out.querySelectorAll('[data-set]').forEach((b) => b.addEventListener('click', () => {
        st[b.getAttribute('data-set')] = b.getAttribute('data-val');
        if (b.getAttribute('data-set') === 'body') st.chosen = null;
        const y = window.scrollY;
        render();
        window.scrollTo(0, y);
        fitLog('chip', b.getAttribute('data-set') + ':' + b.getAttribute('data-val'));
        out.querySelector('[data-set="' + b.getAttribute('data-set') + '"][data-val="' + b.getAttribute('data-val') + '"]')?.focus({ preventScroll: true });
      }));
      out.querySelectorAll('input[type="radio"]').forEach((rb) => rb.addEventListener('change', () => {
        st.chosen = rb.value;
        const y = window.scrollY;
        // On the order page, the window picked in the result is picked above too (ChatGPT review, launch day).
        if (document.querySelector('variant-picker')) selectOnThisPage(rb.value, vehicleFor(st)[0], false);
        render();
        window.scrollTo(0, y);
        fitLog('chip', 'window:' + rb.value);
      }));
      // A tap on a row that is already picked fires no change, so the order page also listens for the tap itself.
      out.querySelectorAll('.pa-fit-row input[type="radio"]').forEach((rb) => rb.addEventListener('click', () => {
        if (document.querySelector('variant-picker')) selectOnThisPage(rb.value, vehicleFor(st)[0], false);
      }));
      out.querySelector('[data-act="edit"]')?.addEventListener('click', () => { input.focus(); input.select(); });
      out.querySelectorAll('[data-order]').forEach((a) => a.addEventListener('click', (e) => {
        if (selectOnThisPage(a.getAttribute('data-order'), a.getAttribute('data-vehicle'))) e.preventDefault();
      }));
    }

    // Order page: if the window picked above is one this fit check says doesn't fit, say so under the picker, with a
    // one-tap switch to a window that fits (it warns, it doesn't block: our team can still confirm by photo).
    function checkPicker() {
      const picker = document.querySelector('variant-picker');
      if (!picker || !lastRows || st.example) return;
      const checked = picker.querySelector('input[data-option-name="Select Window"]:checked') || picker.querySelector('select[name*="Select Window"]');
      const value = checked && checked.value;
      const win = Object.keys(OPTION_VALUES).find((k) => OPTION_VALUES[k] === value);
      const row = win && lastRows.find((x) => x[0] === win);
      let note = document.getElementById('pa-fit-mismatch');
      if (!row || row[1] !== 'no') { if (note) note.hidden = true; return; }
      const best = lastRows.find((x) => FIT.status[x[1]].ok);
      if (!note) {
        note = document.createElement('p');
        note.id = 'pa-fit-mismatch';
        note.className = 'pa-fit-mismatch';
        note.setAttribute('role', 'status');
      }
      (checked.closest('fieldset') || checked).insertAdjacentElement('afterend', note);
      note.hidden = false;
      note.innerHTML = 'Our fit check says <strong>' + FIT.win[win].name + '</strong> doesn’t fit your ' + esc(lastWho) + '. ' +
        (best ? '<button type="button" class="pa-link" data-fit-switch="' + best[0] + '">Switch to ' + FIT.win[best[0]].name + '</button>, or ' : '') +
        '<a class="pa-link" href="' + smsHref(vehicleFor(st)[1]) + '">' + (best ? 'text' : 'Text') + ' us a photo</a> if you think it does.';
      note.querySelector('[data-fit-switch]')?.addEventListener('click', () => selectOnThisPage(best[0], vehicleFor(st)[0], false));
    }
    document.addEventListener('change', (e) => { if (e.target.closest && e.target.closest('variant-picker')) setTimeout(checkPicker, 700); });

    if (example) run(example, true);
  }

  // The pure logic, for tests and later reuse (Chatra). Nothing on the page depends on it.
  window.PranosFit = { FIT, readCar, rowsFor, verdictFor, confirmLine, stateFor, whoFor, vehicleFor, canon, index: getIndex };

  // "Check my car" links (David, 2026-10-05: a small link by the window choices and in the menu): on a page with the
  // fit tool, close the phone menu if it's open, glide to the tool and put the cursor in the box. Elsewhere the link
  // just opens the compatibility page's fit tool.
  document.addEventListener('click', (e) => {
    const a = e.target.closest && e.target.closest('[data-pa-check-car]');
    const sec = a && document.getElementById('will-it-fit');
    if (!sec) return;
    e.preventDefault();
    a.closest('details[open]')?.removeAttribute('open');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    (sec.querySelector('.pa-fit-form') || sec).scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
    const input = sec.querySelector('.pa-fit-form input');
    if (input) setTimeout(() => input.focus({ preventScroll: true }), reduce ? 0 : 500);
  });

  const start = () => document.querySelectorAll('[data-pranos-fit]:not([data-ready])').forEach((el) => { el.setAttribute('data-ready', ''); FitChecker(el); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', start);
})();
